import { useState, useMemo, useEffect, useRef } from 'react';
import { Member, ScoreEvent, useScore } from '../../../context/ScoreContext';

interface Props {
  member: Member;
  onClose: () => void;
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ── Animated counter ──────────────────────────────────────────────────────────
function useAnimatedCount(target: number) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let frame: number;
    const start = 0;
    const duration = 700;
    const startTime = performance.now();
    const animate = (now: number) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + target * eased));
      if (p < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return display;
}

// ── History Tab ───────────────────────────────────────────────────────────────
function HistoryTab({ member, accent, accentRgb }: { member: Member; accent: string; accentRgb: string }) {
  const { scores } = useScore();

  const memberHistory = useMemo(
    () => scores.history.filter(e => e.memberId === member.id),
    [scores.history, member.id],
  );
  const taskHistory = useMemo(
    () => memberHistory.filter(e => e.note?.startsWith('任務完成獎勵')),
    [memberHistory],
  );
  const taskPoints = useMemo(
    () => taskHistory.reduce((s, e) => s + e.points, 0),
    [taskHistory],
  );
  const judgeHistory = useMemo(
    () => memberHistory.filter(e => !e.note?.startsWith('任務完成獎勵')),
    [memberHistory],
  );
  const judgePoints = useMemo(
    () => judgeHistory.reduce((s, e) => s + e.points, 0),
    [judgeHistory],
  );
  const totalPoints = taskPoints + judgePoints;

  const unified = useMemo(
    () => [...memberHistory].sort((a, b) => b.timestamp - a.timestamp),
    [memberHistory],
  );

  const [flashingIds, setFlashingIds] = useState<Set<string>>(new Set());
  const prevLengthRef = useRef(-1);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (prevLengthRef.current === -1) { prevLengthRef.current = unified.length; return; }
    if (unified.length > prevLengthRef.current) {
      const newIds = unified.slice(0, unified.length - prevLengthRef.current).map(e => e.id);
      setFlashingIds(prev => new Set([...prev, ...newIds]));
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashingIds(new Set()), 2000);
    }
    prevLengthRef.current = unified.length;
  }, [unified]);
  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  const CATEGORIES_LABEL: Record<string, string> = {
    battle: '戰鬥積分', chemistry: '默契加分', dreamCoins: '夢幣',
  };

  if (unified.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-16 h-16 rounded-full flex items-center justify-center relative"
          style={{ background: `rgba(${accentRgb},0.06)`, border: `1px dashed rgba(${accentRgb},0.25)` }}
        >
          <i className="ri-trophy-line text-2xl" style={{ color: `rgba(${accentRgb},0.3)` }} />
        </div>
        <p className="text-[#444] text-sm">尚無積分紀錄</p>
        <p className="text-[#2a2a2a] text-xs text-center px-4">完成任務或獲得裁判給分後將顯示在這裡</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div className="rounded-xl p-4 mb-4 border relative overflow-hidden"
        style={{ borderColor: `rgba(${accentRgb},0.2)`, background: `rgba(${accentRgb},0.05)` }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, rgba(${accentRgb},0.6), transparent)` }} />
        <div className="absolute top-0 left-0 w-5 h-5 border-l border-t" style={{ borderColor: `rgba(${accentRgb},0.35)` }} />
        <div className="absolute top-0 right-0 w-5 h-5 border-r border-t" style={{ borderColor: `rgba(${accentRgb},0.35)` }} />
        <p className="text-[10px] tracking-[0.4em] uppercase mb-1 font-cinzel" style={{ color: `rgba(${accentRgb},0.5)` }}>累計貢獻</p>
        <div className="flex items-baseline gap-1.5 mb-3">
          <span className="font-black text-3xl font-orbitron" style={{ color: accent, textShadow: `0 0 16px rgba(${accentRgb},0.5)` }}>{totalPoints}</span>
          <span className="text-[#555] text-xs">分</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: `rgba(${accentRgb},0.08)` }}>
          <div className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min((totalPoints / Math.max(unified.length * 6, 1)) * 100, 100)}%`,
              background: `linear-gradient(90deg, rgba(${accentRgb},0.5), ${accent})`,
              boxShadow: `0 0 8px rgba(${accentRgb},0.4)`,
            }}
          />
        </div>
      </div>

      {/* Entries */}
      <div className="space-y-1.5">
        {unified.map((ev: ScoreEvent) => {
          const isTask = ev.note?.startsWith('任務完成獎勵');
          const isPositive = ev.points >= 0;
          const isNew = flashingIds.has(ev.id);
          const entryAccent = isTask ? accent : '#f0a86e';
          const entryRgb = isTask ? accentRgb : '240,168,110';
          return (
            <div key={ev.id}
              className={`flex items-center gap-3 rounded-xl px-3 py-3 border relative overflow-hidden${isNew ? ' member-score-flash' : ''}`}
              style={{ borderColor: `rgba(${entryRgb},0.12)`, background: '#0c0c14' }}
            >
              <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-xl"
                style={{ background: `linear-gradient(180deg, ${entryAccent}, rgba(${entryRgb},0.3))` }}
              />
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: `rgba(${entryRgb},0.1)`, border: `1px solid rgba(${entryRgb},0.2)` }}
              >
                <i className={`${isTask ? 'ri-gift-2-line' : 'ri-user-star-line'} text-sm`} style={{ color: entryAccent }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold truncate">
                  {isTask ? ev.note.replace('任務完成獎勵：', '') : (ev.note || CATEGORIES_LABEL[ev.category])}
                </p>
                <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                    style={{ background: `rgba(${entryRgb},0.12)`, color: entryAccent }}
                  >
                    {isTask ? '任務獎勵' : '裁判給分'}
                  </span>
                  <span className="text-[#2a2a2a] text-[10px] ml-auto">{formatDate(ev.timestamp)}</span>
                </div>
              </div>
              <span className="font-black text-sm shrink-0 font-orbitron"
                style={{ color: isPositive ? entryAccent : '#e05555', textShadow: `0 0 8px ${isPositive ? `rgba(${entryRgb},0.5)` : 'rgba(224,85,85,0.5)'}` }}
              >
                {isPositive ? '+' : ''}{ev.points}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function MemberViewModal({ member, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<'profile' | 'history'>('profile');
  const { myMemberId, scores } = useScore();

  const isBlack = member.team === 'black';
  const accent = isBlack ? '#d4a843' : '#e0e0e0';
  const accentRgb = isBlack ? '212,168,67' : '224,224,224';

  const memberScoreHistory = useMemo(
    () => scores.history.filter(e => e.memberId === member.id),
    [scores.history, member.id],
  );
  const taskPoints = useMemo(
    () => memberScoreHistory.filter(e => e.note?.startsWith('任務完成獎勵')).reduce((s, e) => s + e.points, 0),
    [memberScoreHistory],
  );
  const judgePoints = useMemo(
    () => memberScoreHistory.filter(e => !e.note?.startsWith('任務完成獎勵')).reduce((s, e) => s + e.points, 0),
    [memberScoreHistory],
  );
  const totalPoints = taskPoints + judgePoints;

  const animatedTotal = useAnimatedCount(totalPoints);
  const animatedTask = useAnimatedCount(taskPoints);
  const animatedJudge = useAnimatedCount(judgePoints);

  const isSelf = myMemberId === member.id;
  // 小主人任務資訊僅在個人編輯頁（MemberProfileModal）顯示，出征名單不顯示

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(3,3,12,0.88)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      />

      {/* Modal card */}
      <div
        className="relative z-10 w-full max-w-sm rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #0e0d18 0%, #08080f 100%)',
          border: `1.5px solid rgba(${accentRgb},0.2)`,
          maxHeight: '88dvh',
        }}
      >
        {/* Top glow line */}
        <div className="absolute top-0 left-0 right-0 h-px pointer-events-none z-20"
          style={{ background: `linear-gradient(90deg, transparent, rgba(${accentRgb},0.8), transparent)` }}
        />

        {/* Background aurora */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-48 blur-[60px] opacity-20"
            style={{ background: `radial-gradient(ellipse, rgba(${accentRgb},0.7), transparent 70%)` }}
          />
          <div className="absolute inset-0"
            style={{ backgroundImage: `radial-gradient(circle, rgba(${accentRgb},0.025) 1px, transparent 1px)`, backgroundSize: '24px 24px' }}
          />
        </div>

        {/* ── HERO BANNER ── */}
        <div className="relative px-5 pt-7 pb-5 shrink-0">
          {/* Corner brackets */}
          <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 pointer-events-none" style={{ borderColor: `rgba(${accentRgb},0.5)` }} />
          <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 pointer-events-none" style={{ borderColor: `rgba(${accentRgb},0.5)` }} />

          {/* Close btn */}
          <div className="flex justify-end mb-4">
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all active:scale-90"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid #252535', color: '#555' }}
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>

          {/* Avatar + Identity */}
          <div className="flex items-center gap-4 mb-5">
            <div className="relative shrink-0">
              <div className="absolute -inset-1.5 rounded-full"
                style={{
                  background: `conic-gradient(${accent}, transparent, ${accent})`,
                  animation: 'modalRingRotate 5s linear infinite',
                  opacity: 0.6,
                }}
              />
              <div className="absolute -inset-1 rounded-full blur-sm opacity-40"
                style={{ background: `radial-gradient(circle, rgba(${accentRgb},0.8), transparent)` }}
              />
              {member.avatarUrl ? (
                <img src={member.avatarUrl} alt={member.name}
                  className="relative w-16 h-16 rounded-full object-cover object-top z-10"
                  style={{ border: `2px solid rgba(${accentRgb},0.5)` }}
                />
              ) : (
                <div className="relative w-16 h-16 rounded-full flex items-center justify-center text-2xl font-black z-10"
                  style={{
                    background: `radial-gradient(circle at 35% 35%, rgba(${accentRgb},0.28), rgba(${accentRgb},0.06))`,
                    color: accent,
                    border: `2px solid rgba(${accentRgb},0.45)`,
                  }}
                >
                  {member.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-[#0e0d18] bg-[#7ec8a0] z-20"
                style={{ boxShadow: '0 0 8px rgba(126,200,160,0.7)' }}
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                <span className="text-[9px] font-black tracking-[0.35em] px-2.5 py-0.5 rounded-full font-cinzel"
                  style={{ background: accent, color: '#0d0d0d', boxShadow: `0 0 10px rgba(${accentRgb},0.5)` }}
                >
                  {isBlack ? 'BLACK' : 'WHITE'}
                </span>
                {isSelf && (
                  <span className="text-[9px] font-black tracking-widest px-2 py-0.5 rounded-full"
                    style={{ background: '#7ec8a018', color: '#7ec8a0', border: '1px solid #7ec8a040' }}
                  >
                    你
                  </span>
                )}
              </div>
              <h3 className="text-white text-xl font-black leading-tight truncate"
                style={{ textShadow: `0 0 20px rgba(${accentRgb},0.3)` }}
              >
                {member.name}
              </h3>
              <p className="text-[10px] tracking-[0.35em] mt-0.5 font-cinzel"
                style={{ color: `rgba(${accentRgb},0.45)` }}
              >
                {isBlack ? 'BLACK TEAM' : 'WHITE TEAM'} · MEMBER
              </p>
            </div>
          </div>

          {/* Score stats */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: animatedTotal, label: '總積分', icon: 'ri-sword-fill', color: accent, rgb: accentRgb, glow: true },
              { value: animatedJudge >= 0 ? `+${animatedJudge}` : `${animatedJudge}`, label: '裁判貢獻', icon: 'ri-scales-3-line', color: '#f0a86e', rgb: '240,168,110', glow: false },
              { value: `+${animatedTask}`, label: '任務積分', icon: 'ri-gift-2-line', color: accent, rgb: accentRgb, glow: false },
            ].map((stat, i) => (
              <div key={i}
                className="rounded-xl py-3 px-2 text-center relative overflow-hidden"
                style={{
                  background: i === 0 ? `rgba(${accentRgb},0.08)` : '#0c0c14',
                  border: `1px solid ${i === 0 ? `rgba(${accentRgb},0.25)` : `rgba(${stat.rgb},0.1)`}`,
                  boxShadow: i === 0 ? `0 0 18px rgba(${accentRgb},0.12)` : 'none',
                }}
              >
                {i === 0 && (
                  <div className="absolute top-0 left-0 right-0 h-px"
                    style={{ background: `linear-gradient(90deg, transparent, rgba(${accentRgb},0.6), transparent)` }}
                  />
                )}
                <div className="w-5 h-5 flex items-center justify-center mx-auto mb-1.5">
                  <i className={`${stat.icon} text-xs`} style={{ color: stat.color }} />
                </div>
                <div className="font-black text-base font-orbitron leading-none"
                  style={{
                    color: stat.color,
                    textShadow: stat.glow ? `0 0 12px rgba(${accentRgb},0.6)` : 'none',
                  }}
                >
                  {stat.value}
                </div>
                <div className="text-[9px] mt-1 tracking-wide" style={{ color: '#444' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="h-px mx-5 shrink-0" style={{ background: `linear-gradient(90deg, transparent, rgba(${accentRgb},0.2), transparent)` }} />

        {/* ── BODY ── */}
        <div className="flex flex-col flex-1 overflow-hidden px-5 pb-5 pt-4">
          {/* Tab switcher */}
          <div className="flex gap-1 p-0.5 rounded-xl mb-4 shrink-0"
            style={{ background: '#0a0a14', border: `1px solid rgba(${accentRgb},0.12)` }}
          >
            {(['profile', 'history'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-black tracking-wide cursor-pointer transition-all whitespace-nowrap relative overflow-hidden"
                style={activeTab === tab
                  ? { background: `rgba(${accentRgb},0.12)`, color: accent, border: `1px solid rgba(${accentRgb},0.3)`, boxShadow: `0 0 12px rgba(${accentRgb},0.4)` }
                  : { color: '#444', border: '1px solid transparent' }
                }
              >
                {activeTab === tab && (
                  <div className="absolute top-0 left-0 right-0 h-px"
                    style={{ background: `linear-gradient(90deg, transparent, rgba(${accentRgb},0.7), transparent)` }}
                  />
                )}
                <i className={`${tab === 'profile' ? 'ri-user-line' : 'ri-bar-chart-line'} text-sm`} />
                {tab === 'profile' ? '個人資料' : '積分紀錄'}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {activeTab === 'profile' ? (
              <div className="space-y-3">
                {/* Goal card */}
                <div className="rounded-xl p-4 border relative overflow-hidden"
                  style={{ borderColor: `rgba(${accentRgb},0.18)`, background: `rgba(${accentRgb},0.04)` }}
                >
                  <div className="absolute top-0 left-0 right-0 h-px"
                    style={{ background: `linear-gradient(90deg, transparent, rgba(${accentRgb},0.4), transparent)` }}
                  />
                  <div className="absolute top-0 left-0 w-4 h-4 border-l border-t" style={{ borderColor: `rgba(${accentRgb},0.3)` }} />
                  <div className="absolute top-0 right-0 w-4 h-4 border-r border-t" style={{ borderColor: `rgba(${accentRgb},0.3)` }} />
                  <div className="flex items-center gap-2 mb-2.5">
                    <i className="ri-flag-2-line text-xs" style={{ color: `rgba(${accentRgb},0.7)` }} />
                    <span className="text-[10px] tracking-[0.35em] uppercase font-cinzel" style={{ color: `rgba(${accentRgb},0.5)` }}>個人目標</span>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: member.goal ? '#ccc' : '#2a2a3a' }}>
                    {member.goal || <em>尚未設定目標</em>}
                  </p>
                </div>

                {/* Team info */}
                <div className="rounded-xl p-4 border overflow-hidden relative"
                  style={{ borderColor: `rgba(${accentRgb},0.12)`, background: '#0a0a14' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full shrink-0"
                      style={{ background: `rgba(${accentRgb},0.12)`, border: `1.5px solid rgba(${accentRgb},0.3)`, boxShadow: `0 0 14px rgba(${accentRgb},0.2)` }}
                    >
                      <i className={`${isBlack ? 'ri-shield-fill' : 'ri-sword-fill'} text-sm`} style={{ color: accent }} />
                    </div>
                    <div>
                      <p className="text-white text-sm font-black">{isBlack ? '黑隊' : '白隊'}</p>
                      <p className="text-[10px] tracking-widest font-cinzel" style={{ color: `rgba(${accentRgb},0.45)` }}>
                        {isBlack ? 'BLACK TEAM' : 'WHITE TEAM'}
                      </p>
                    </div>
                    <div className="ml-auto flex items-center gap-1.5">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7ec8a0] opacity-60" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#7ec8a0]" />
                      </span>
                      <span className="text-[#7ec8a0] text-[10px] tracking-wide">在線</span>
                    </div>
                  </div>
                </div>

                {/* Close button */}
                <button onClick={onClose}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap active:scale-[0.98]"
                  style={{ background: 'transparent', border: `1px solid rgba(${accentRgb},0.2)`, color: `rgba(${accentRgb},0.6)` }}
                >
                  <i className="ri-arrow-down-s-line" />
                  關閉
                </button>
              </div>
            ) : (
              <HistoryTab member={member} accent={accent} accentRgb={accentRgb} />
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalRingRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes member-score-flash {
          0% { background: rgba(126,200,160,0.18); }
          100% { background: #0c0c14; }
        }
        .member-score-flash { animation: member-score-flash 1.5s ease-out forwards; }
      `}</style>
    </div>
  );
}
